import { useEffect } from 'react';
import type { NavigateFn } from '@tanstack/react-router';
import type { MutableRefObject } from 'react';

import { extractTabSuffix } from '&/utils/url';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseSiteDetailNavigationOptions {
  hasSynced: boolean;
  masterSiteId: string | null;
  masterSpaceId: string | null;
  navigate: NavigateFn;
  pathname: string;
  siteId: string;
  skipNextScopeChange: MutableRefObject<boolean>;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useSiteDetailNavigation({
  hasSynced,
  masterSiteId,
  masterSpaceId,
  navigate,
  pathname,
  siteId,
  skipNextScopeChange,
}: UseSiteDetailNavigationOptions): void {
  useEffect((): void => {
    if (skipNextScopeChange.current) {
      skipNextScopeChange.current = false;
      return;
    }

    if (!hasSynced) return;

    if (!masterSpaceId && !masterSiteId) {
      void navigate({ to: '/settings' });
      return;
    }

    if (masterSpaceId && !masterSiteId) {
      void navigate({ params: { spaceId: masterSpaceId }, to: '/settings/spaces/$spaceId' });
      return;
    }

    if (masterSiteId && masterSiteId !== siteId) {
      const tabSuffix = extractTabSuffix(pathname, `/settings/sites/${siteId}`);
      if (tabSuffix) {
        void navigate({ to: `/settings/sites/${masterSiteId}${tabSuffix}` });
      } else {
        void navigate({ params: { siteId: masterSiteId }, to: '/settings/sites/$siteId' });
      }
    }
  }, [hasSynced, masterSiteId, masterSpaceId, navigate, pathname, siteId, skipNextScopeChange]);
}
